import React, { useEffect } from 'react';
import { Box, Form, TextArea, Button, FormExtendedEvent, Text } from 'grommet';
import { LoadingDots } from '../LoadingDots';

export interface IMentionCandidate {
  id: string;
  label: string;
}

export interface IUserInputProps {
  handleSubmit: (e: FormExtendedEvent) => void;
  loading: boolean;
  handleEnter: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  textAreaRef: React.RefObject<HTMLTextAreaElement | null>;
  query: string;
  setQuery: (query: string) => void;
  gridArea?: string;
  mentionCandidates?: IMentionCandidate[];
  placeholder?: string;
  disabled?: boolean;
}

// Only recognizes an @-mention actively being typed at the end of the current text —
// a deliberate simplification over full caret-position tracking (not needed for a
// single-line-ish chat composer).
const MENTION_PATTERN = /(?:^|\s)@([a-zA-Z0-9._-]*)$/;

export const UserInput = ({
  handleSubmit,
  loading,
  handleEnter,
  textAreaRef,
  query,
  setQuery,
  gridArea,
  mentionCandidates = [],
  placeholder = 'Enter your question here...',
  disabled = false,
}: IUserInputProps) => {
  const mentionMatch = MENTION_PATTERN.exec(query);
  useEffect(() => {
    const area = textAreaRef.current;
    if (!area) return;
    area.style.height = 'auto';
    area.style.height = `${Math.min(area.scrollHeight, 144)}px`;
    area.style.overflowY = area.scrollHeight > 144 ? 'auto' : 'hidden';
  }, [query, textAreaRef]);
  const activeQuery = mentionMatch?.[1];
  const suggestions =
    activeQuery !== undefined && mentionCandidates.length
      ? mentionCandidates.filter((c) => c.label.toLowerCase().startsWith(activeQuery.toLowerCase())).slice(0, 6)
      : [];

  const selectMention = (candidate: IMentionCandidate) => {
    const match = MENTION_PATTERN.exec(query);
    if (!match) return;
    const leadingChar = match[0][0] === '@' ? '' : match[0][0];
    const prefix = query.slice(0, match.index) + leadingChar;
    setQuery(`${prefix}@[${candidate.label}](${candidate.id}) `);
    textAreaRef.current?.focus();
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Shift+Enter inserts a newline — the textarea's default action, so we return without
    // preventDefault. Checked before the mention branch so it also wins when suggestions
    // are open, leaving plain Enter to select the first one.
    if (e.key === 'Enter' && e.shiftKey) return;
    if (e.key === 'Enter' && suggestions.length > 0) {
      e.preventDefault();
      selectMention(suggestions[0]);
      return;
    }
    handleEnter(e);
  };

  return (
    <Box
      gridArea={gridArea}
      fill="horizontal"
      pad={{ vertical: '10px', horizontal: '12px' }}
      border={{ side: 'top', color: 'border-divider' }}
      style={{ position: 'relative' }}
    >
      {suggestions.length > 0 && (
        <Box
          background="white"
          border={{ color: '#d9d9e3' }}
          round="small"
          elevation="small"
          style={{ position: 'absolute', bottom: '100%', left: '12px', right: '12px', zIndex: 20 }}
        >
          {suggestions.map((s) => (
            <Box
              key={s.id}
              pad={{ vertical: 'xsmall', horizontal: 'small' }}
              hoverIndicator
              style={{ cursor: 'pointer' }}
              onClick={() => selectMention(s)}
            >
              <Text size="small">@{s.label}</Text>
            </Box>
          ))}
        </Box>
      )}
      <Form onSubmit={handleSubmit}>
        {/* A bordered box to type in, and a Send button beside it */}
        <Box direction="row" gap="8px" align="end">
          <Box flex>
            <TextArea
              disabled={loading || disabled}
              onKeyDown={onKeyDown}
              ref={textAreaRef}
              autoFocus={false}
              focusIndicator={false}
              rows={1}
              maxLength={512}
              id="userInput"
              name="userInput"
              placeholder={loading ? 'Waiting for response...' : placeholder}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              fill
            />
          </Box>
          <Box flex={false} margin={{ bottom: '2px' }}>
            <Button type="submit" primary disabled={loading || disabled || !query.trim()}>
              <Box pad={{ vertical: '6px', horizontal: '14px' }}>
                {loading ? (
                  <LoadingDots color="#fff" />
                ) : (
                  <Text size="14px" weight={600}>
                    Send
                  </Text>
                )}
              </Box>
            </Button>
          </Box>
        </Box>
        <Box direction="row" justify="between" margin={{ top: 'xsmall' }}>
          <Text size="12px" color="#777">Enter to send · Shift+Enter for a new line</Text>
          {query.length >= 450 && <Text size="12px" color={query.length >= 500 ? '#c62828' : '#777'}>{512 - query.length} left</Text>}
        </Box>
      </Form>
    </Box>
  );
};
